import { useState, useCallback, useEffect } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import _ from 'lodash'
import clx from 'classnames'
import { FormLoader, Tip, Button, Toast } from '@vzmi/shp_shared_compo'
import { EMPTY_VALUE, DEFAULT_NO_FOUND_DESCRIPTION } from '../../../../constants/globalTypes'
import {
  RadioGroupInputField,
  TextareaCountField
} from '../../../Common/Form'
import { getDashboardDateTime } from '../../../../lib/MomentHelper'
import { numeralFormat } from '../../../../../lib/renderUtil'
import safeAwait from '../../../../../lib/safeAwait'
import errorHelper, { httpHandler } from '../../../../../lib/errorHelper'
import {
  fetchSeSaleStatsByDate,
  fetchSeListingPageViews,
  fetchSePaginationBestsellers
} from '../../../../actions'
import Block from '../../../Common/NewDashboard/Block/Block'
import FormRow from '../../../Common/NewDashboard/FormRow/FormRow'
import DateTimeRange from '../../../Common/NewDashboard/DateTimeRange/DateTimeRange'
import Tabs from '../../../Common/NewDashboard/Tabs/Tabs'
import Divider from '../../../Common/NewDashboard/Divider/Divider'
import SummaryBanner from '../../../Common/NewDashboard/Chart/SummaryBanner/SummaryBanner'
import NoData from '../../../Common/NoData/NoData'
import {
  formDecorator,
  Field,
  FormValueSelector
} from '../../../Common/forms'
import { removeNotAlphanumericAndComma } from '../../../../lib/parser'
import {
  LISTING_TYPE,
  LISTING_TYPE_OPTIONS
} from '../../../Dashboard/OperatingDashboard/constants/listing'

import PerformanceChart, {
  FIELD as CHART_FIELD
} from './PerformanceChart/PerformanceChart'
import RankingTable from './RankingTable/RankingTable'
import Sort from '../../../Common/NewDashboard/Sort/Sort'
import PartNotFound from '../../../Common/NewDashboard/PartNotFound/PartNotFound'
import { getInitialTimeRange, getBestsellerRankings, getListingIdsArray } from './helper'
import { RANKING_SIZE, LISTING_IDS_LIMIT } from './constants'
import styles from './index.css'

export const FIELD = {
  LISTING_TYPE: 'listingType',
  LISTING_IDS: 'listingIds'
}

const PAGE_VIEWS_API_LIMIT = 120

export const INIT_VALUES = {
  [FIELD.LISTING_TYPE]: LISTING_TYPE.ALL,
  [FIELD.LISTING_IDS]: ''
}

export const TAB = {
  PERF_CHART: 'perf_chart',
  RANKING_TABLE: 'ranking_table'
}

const TAB_MAP = {
  [TAB.PERF_CHART]: '訂購金額／數量',
  [TAB.RANKING_TABLE]: '賣場排行'
}

const SUMMARY_MAP = {
  [CHART_FIELD.AMOUNT]: '訂購總金額',
  [CHART_FIELD.SOLD_QUANTITY]: '訂購總數量'
}

const LISTING_ID_NO_FOUND_DESCRIPTION = '查無編號，請重新確認'
const SORT_TYPE = {
  AMOUNT: 'soldAmount',
  QUANTITY: 'soldQuantity',
  PAGE_VIEW: 'pageView',
  CONVERSION_RATE: 'conversionRate',
  UNIQUE_USER: 'uniqueUser'
}

const SORT_TYPE_TEXT = {
  [SORT_TYPE.AMOUNT]: '訂購金額',
  [SORT_TYPE.QUANTITY]: '訂購量',
  [SORT_TYPE.PAGE_VIEW]: '瀏覽量',
  [SORT_TYPE.CONVERSION_RATE]: '轉換率',
  [SORT_TYPE.UNIQUE_USER]: '訪客數'
}

export const SORT_OPTIONS = [
  {
    value: SORT_TYPE.AMOUNT,
    text: SORT_TYPE_TEXT[SORT_TYPE.AMOUNT]
  },
  {
    value: SORT_TYPE.QUANTITY,
    text: SORT_TYPE_TEXT[SORT_TYPE.QUANTITY]
  },
  {
    value: SORT_TYPE.PAGE_VIEW,
    text: SORT_TYPE_TEXT[SORT_TYPE.PAGE_VIEW]
  },
  {
    value: SORT_TYPE.CONVERSION_RATE,
    text: SORT_TYPE_TEXT[SORT_TYPE.CONVERSION_RATE]
  },
  {
    value: SORT_TYPE.UNIQUE_USER,
    text: SORT_TYPE_TEXT[SORT_TYPE.UNIQUE_USER]
  }
]

const MAX_LISTING_ID_LENGTH = 14

const validateListingIds = (listingIds) => {
  if (!listingIds) {
    return '請輸入賣場編號'
  }

  const parsedIds = getListingIdsArray(listingIds)

  if (parsedIds.length > LISTING_IDS_LIMIT) {
    return `最多輸入${LISTING_IDS_LIMIT}筆`
  }

  const isOversizeId = parsedIds.some(id => id.length > MAX_LISTING_ID_LENGTH)
  if (isOversizeId) {
    return '賣場編號長度超過最大值,請重新檢查'
  }

  return null
}

export const CONFIG = {
  form: 'SELLER_SALES_PERFORMANCE_FORM',
  validate: (values) => {
    const errors = {}
    const listingType = _.get(values, FIELD.LISTING_TYPE)
    const listingIds = _.get(values, FIELD.LISTING_IDS)

    if (listingType !== LISTING_TYPE.ORDER) {
      return errors
    }

    const errorMessage = validateListingIds(listingIds)
    if (errorMessage) {
      errors[FIELD.LISTING_IDS] = errorMessage
    }

    return errors
  },
  initialValues: INIT_VALUES
}

export const getCount = (props) => {
  const inputValue = _.get(props, 'input.value', '')
  return _.size(getListingIdsArray(inputValue))
}

const transformSummary = (summary) => {
  const amount = _.toNumber(_.get(summary, CHART_FIELD.AMOUNT, 0))
  const soldQuantity = _.toNumber(_.get(summary, CHART_FIELD.SOLD_QUANTITY, 0))

  return [
    {
      label: SUMMARY_MAP[CHART_FIELD.AMOUNT],
      value: `$${numeralFormat(amount, { allowZero: true })}`
    },
    {
      label: SUMMARY_MAP[CHART_FIELD.SOLD_QUANTITY],
      value: numeralFormat(soldQuantity, { allowZero: true })
    }
  ]
}

const checkIsNoPerformanceFound = (summary) => {
  const amount = _.get(summary, CHART_FIELD.AMOUNT, 0)
  const soldQuantity = _.get(summary, CHART_FIELD.SOLD_QUANTITY, 0)
  return _.toNumber(amount) === 0 && soldQuantity === 0
}

const checkIsNoListingDataFound = (listingType, performances) => {
  if (listingType !== LISTING_TYPE.ORDER) {
    return false
  }
  return _.isEmpty(performances)
}

const useSalesPerformance = (actions) => {
  const { fetchSeSaleStatsByDate, fetchSeListingPageViews, fetchSePaginationBestsellers } = actions
  const [loading, setLoading] = useState(false)
  const [salesPerformance, setSalesPerformance] = useState(null)

  const fetchData = useCallback(async (params = {}) => {
    setLoading(true)

    const initialTimeRange = getInitialTimeRange()
    const listingIds = _.get(params, 'listingIds', '')
    const listingType = _.get(params, 'listingType', LISTING_TYPE.ALL)

    const saleStatsParams = {
      fields: '+newOrder',
      reportType: 'hour',
      reportDateStart: initialTimeRange.reportDateStart,
      reportDateEnd: initialTimeRange.reportDateEnd
    }

    if (listingType === LISTING_TYPE.ORDER && listingIds) {
      saleStatsParams.listingIds = listingIds
    }

    const [error, payload] = await safeAwait(
      fetchSeSaleStatsByDate(saleStatsParams).then(httpHandler)
    )

    if (error) {
      const errorMessage = errorHelper.parseErrorMessage(error)
      Toast.error(errorMessage)
      setLoading(false)
      return
    }

    const newOrderData = _.get(payload, 'newOrder', {})
    const {
      reportDateStart,
      reportDateEnd,
      statistics = [],
      summary = {}
    } = newOrderData

    const pageViewParams = {
      reportHourStart: reportDateStart,
      reportHourEnd: reportDateEnd,
      limit: PAGE_VIEWS_API_LIMIT
    }

    const [pageViewError, pageViewPayload] = await safeAwait(
      fetchSeListingPageViews(pageViewParams).then(httpHandler)
    )

    if (pageViewError) {
      const errorMessage = errorHelper.parseErrorMessage(pageViewError)
      Toast.error(errorMessage)
      setLoading(false)
      return
    }

    const pageViews = _.get(pageViewPayload, 'pageViews', [])
    const pageViewMap = _.reduce(pageViews, (acc, item) => {
      const reportDate = _.get(item, 'reportDate')
      const pageView = +_.get(item, 'pageView', 0)
      const viewerQuantity = +_.get(item, 'viewerQuantity', 0)

      if (!acc[reportDate]) {
        acc[reportDate] = {
          pageView: 0,
          totalViewerQuantity: 0
        }
      }

      acc[reportDate].pageView += pageView
      acc[reportDate].totalViewerQuantity += viewerQuantity

      return acc
    }, {})

    const convertedStatistics = _.map(statistics, item => {
      const reportDate = _.get(item, 'reportDate')
      const pageViewData = _.get(pageViewMap, reportDate, {})

      return {
        reportDate,
        amount: +_.get(item, 'amount', 0),
        soldQuantity: +_.get(item, 'soldQuantity', 0),
        pageView: +_.get(pageViewData, 'pageView', 0),
        totalViewerQuantity: +_.get(pageViewData, 'totalViewerQuantity', 0)
      }
    })

    const sortType = _.get(params, 'sortType', SORT_TYPE.AMOUNT)

    const [bestsellerError, bestsellerResult] = await getBestsellerRankings({
      fetchSePaginationBestsellers,
      reportDateStart,
      reportDateEnd,
      listingType,
      listingIds,
      sortType,
      formParams: params
    })

    if (bestsellerError) {
      const errorMessage = errorHelper.parseErrorMessage(bestsellerError)
      Toast.error(errorMessage)
      setLoading(false)
      return
    }

    const { rankings, notFoundListingIds } = bestsellerResult

    setSalesPerformance({
      datetimeRange: [reportDateStart, reportDateEnd],
      salesSummaries: transformSummary(summary),
      performances: convertedStatistics,
      rankings,
      notFoundListingIds,
      isNoPerformanceFound: checkIsNoPerformanceFound(summary),
      isNoListingDataFound: checkIsNoListingDataFound(listingType, convertedStatistics)
    })
    setLoading(false)
  }, [fetchSeSaleStatsByDate, fetchSeListingPageViews, fetchSePaginationBestsellers])

  const fetchRankings = useCallback(async (params = {}) => {
    if (!salesPerformance) {
      return
    }

    setLoading(true)

    const datetimeRange = _.get(salesPerformance, 'datetimeRange', [])
    const [reportDateStart, reportDateEnd] = datetimeRange
    const listingType = _.get(params, 'listingType', LISTING_TYPE.ALL)
    const listingIds = _.get(params, 'listingIds', '')
    const sortType = _.get(params, 'sortType', SORT_TYPE.AMOUNT)

    const [bestsellerError, bestsellerResult] = await getBestsellerRankings({
      fetchSePaginationBestsellers,
      reportDateStart,
      reportDateEnd,
      listingType,
      listingIds,
      sortType,
      formParams: params
    })

    if (bestsellerError) {
      const errorMessage = errorHelper.parseErrorMessage(bestsellerError)
      Toast.error(errorMessage)
      setLoading(false)
      return
    }

    const { rankings, notFoundListingIds } = bestsellerResult

    setSalesPerformance({
      ...salesPerformance,
      rankings,
      notFoundListingIds
    })
    setLoading(false)
  }, [salesPerformance, fetchSePaginationBestsellers])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    loading,
    salesPerformance,
    fetchData,
    fetchRankings
  }
}

const SalesPerformance = (props) => {
  const { handleSubmit, actions } = props
  const {
    loading,
    salesPerformance,
    fetchData,
    fetchRankings
  } = useSalesPerformance(actions)

  const {
    datetimeRange = [],
    salesSummaries = [],
    performances = [],
    rankings = [],
    notFoundListingIds = '',
    isNoPerformanceFound = false,
    isNoListingDataFound = false
  } = salesPerformance || {}

  const [selectedTab, setSelectedTab] = useState(TAB.PERF_CHART)
  const [rankingSortType, setRankingSortType] = useState(SORT_TYPE.AMOUNT)
  const [researchedFormData, setResearchedFormData] = useState({
    listingType: LISTING_TYPE.ALL,
    listingIds: ''
  })

  const handleQuery = useCallback((values) => {
    const { listingType, listingIds } = values
    setResearchedFormData(values)
    fetchData({ listingType, listingIds, sortType: rankingSortType })
  }, [fetchData, rankingSortType])

  const handleRankingTableSortChange = useCallback((event) => {
    const newSortType = _.get(event, 'target.value', '')
    if (newSortType === rankingSortType) {
      return
    }
    setRankingSortType(newSortType)

    const { listingType, listingIds } = researchedFormData
    fetchRankings({ listingType, listingIds, sortType: newSortType })
  }, [rankingSortType, researchedFormData, fetchRankings])

  const rankingColumnProps = {
    rankingSize: RANKING_SIZE,
    width: 35
  }

  const tableNoFoundDescription = isNoListingDataFound
    ? LISTING_ID_NO_FOUND_DESCRIPTION
    : DEFAULT_NO_FOUND_DESCRIPTION

  const [startDateTime, endDateTime] = datetimeRange

  const chartNoFoundDescription = isNoPerformanceFound
    ? isNoListingDataFound
      ? LISTING_ID_NO_FOUND_DESCRIPTION
      : DEFAULT_NO_FOUND_DESCRIPTION
    : ''
  const { listingType, listingIds } = researchedFormData

  const isPartListingIdNoFoundVisible = (
    !isNoListingDataFound && !_.isEmpty(notFoundListingIds)
  )

  const gdidParam = listingIds && listingType === LISTING_TYPE.ORDER
    ? `?gdids=${listingIds}`
    : ''

  return (
    <div data-role='sales-performance'>
      <FormLoader isLoading={loading}>
        <Block noMarginTop>
          <form onSubmit={handleSubmit(handleQuery)}>
            <FormRow
              label='時間區間'
              labelAlign='top'
              contentClassName={styles.dateTimeRangeWrapper}
            >
              <div className={styles.datetimeRangeArea}>
                {(startDateTime && endDateTime)
                  ? (
                    <>
                      {getDashboardDateTime(startDateTime)}
                      <DateTimeRange className={styles.datetimeRange} />
                      {getDashboardDateTime(endDateTime)}
                    </>
                  )
                  : (
                    <span>
                      {EMPTY_VALUE}
                      <DateTimeRange className={styles.timeRange} />
                      {EMPTY_VALUE}
                    </span>
                  )}
              </div>
              <Tip type='info'>
                以時為單位，統計過去24小時業績，每日08:00 - 23:00更新
              </Tip>
            </FormRow>
            <FormRow
              label='賣場'
              labelAlign='top'
            >
              <Field
                key={FIELD.LISTING_TYPE}
                name={FIELD.LISTING_TYPE}
                data-role={FIELD.LISTING_TYPE}
                className={styles.listingType}
                component={RadioGroupInputField}
                buttonStyle='outline'
                options={LISTING_TYPE_OPTIONS}
              />
            </FormRow>
            <FormValueSelector fieldName={FIELD.LISTING_TYPE}>
              {listingType => {
                const isFieldVisible = listingType === LISTING_TYPE.ORDER
                return (
                  <FormRow
                    className={clx(
                      styles.listingIdRow,
                      { hidden: !isFieldVisible }
                    )}
                  >
                    <Field
                      name={FIELD.LISTING_IDS}
                      data-role={FIELD.LISTING_IDS}
                      countLimit={LISTING_IDS_LIMIT}
                      customizeGetCount={getCount}
                      component={TextareaCountField}
                      className={styles.listingId}
                      placeholder={`請輸入賣場編號，以逗號或 ENTER 分隔，最多輸入 ${LISTING_IDS_LIMIT} 筆`}
                      parse={removeNotAlphanumericAndComma}
                    />
                  </FormRow>
                )
              }}
            </FormValueSelector>
            <FormRow labelAlign='top'>
              <Button
                type='submit'
                data-role='search-btn'
                size='large'
                className={styles.searchBtn}
              >
                查詢
              </Button>
            </FormRow>
          </form>
        </Block>
        {isPartListingIdNoFoundVisible && (
          <PartNotFound>
            查無下列賣場編號，如需查詢，請重新確認：
            {notFoundListingIds}
          </PartNotFound>
        )}
        <Tabs
          activeKey={selectedTab}
          onChange={setSelectedTab}
          items={[
            {
              label: TAB_MAP[TAB.PERF_CHART],
              key: TAB.PERF_CHART,
              children: (
                <Block noMarginBottom>
                  {isNoPerformanceFound
                    ? (
                      <NoData
                        className={styles.chartNoData}
                        description={chartNoFoundDescription}
                      />
                    )
                    : (
                      <>
                        <SummaryBanner summaries={salesSummaries} />
                        <PerformanceChart
                          data={performances}
                        />
                        <Divider />
                        <Tip type='info'>以時為單位，統計過去24小時業績</Tip>
                      </>
                    )}
                </Block>
              )
            },
            {
              label: TAB_MAP[TAB.RANKING_TABLE],
              key: TAB.RANKING_TABLE,
              children: (
                <>
                  <Sort
                    id='sales-performance'
                    options={SORT_OPTIONS}
                    onChange={handleRankingTableSortChange}
                    sortType={rankingSortType}
                  />
                  <RankingTable
                    data={rankings}
                    rankingColumnProps={rankingColumnProps}
                    noFoundDescription={tableNoFoundDescription}
                    readmoreProps={{
                      href: `/seller/items/salesPerformance${gdidParam}`
                    }}
                  />
                  <Tip type='info'>賣場排行，顯示至前5名</Tip>
                </>
              )
            }
          ]}
        />
      </FormLoader>
    </div>
  )
}

const mapDispatchToProps = (dispatch) => {
  const actions = {
    fetchSeSaleStatsByDate,
    fetchSeListingPageViews,
    fetchSePaginationBestsellers
  }
  return {
    actions: bindActionCreators(actions, dispatch)
  }
}

const SalesPerformanceWithDecorator = formDecorator(CONFIG)(SalesPerformance)

export default connect(
  null,
  mapDispatchToProps
)(SalesPerformanceWithDecorator)
